/**
 * ResumeTargetWorkspace — /resume-mastery/workspace/targets/:id
 *
 * The per-target step workflow. Fetches the target detail and renders the
 * STEP_CONFIG steps, grouped by `group` (in config order), each as a StepCard.
 *
 * The header carries the company / role, an editable status Select (patched
 * straight back via updateResumeTarget), and the gap score once it exists.
 * When any step runs we invalidate both the target query (to flip cards to
 * "done" and refresh the gap score) and the usage query (a run consumes quota).
 */

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getResumeTarget,
  updateResumeTarget,
  downloadResumeDocx,
  ResumeTargetStatus,
} from "../api/client";
import { STEP_CONFIG } from "../components/resume/stepConfig";
import type { StepConfig } from "../components/resume/stepConfig";
import StepCard from "../components/resume/StepCard";
import ExemplarPanel from "../components/resume/ExemplarPanel";
import TrajectoryChart from "../components/resume/TrajectoryChart";

// coral via theme.palette.primary — per-product accent deferred (spec D2)
const WORKSPACE_PATH = "/resume-mastery/workspace";

const STATUS_OPTIONS: ResumeTargetStatus[] = [
  "researching",
  "applied",
  "interviewing",
  "offer",
  "rejected",
];

/** Steps grouped by `group`, preserving the order groups first appear in. */
function groupSteps(): { group: string; steps: StepConfig[] }[] {
  const order: string[] = [];
  const byGroup = new Map<string, StepConfig[]>();
  for (const cfg of STEP_CONFIG) {
    if (!byGroup.has(cfg.group)) {
      byGroup.set(cfg.group, []);
      order.push(cfg.group);
    }
    byGroup.get(cfg.group)!.push(cfg);
  }
  return order.map((group) => ({ group, steps: byGroup.get(group)! }));
}

export default function ResumeTargetWorkspace() {
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation("pages-account");

  const {
    data: target,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["resume", "target", id],
    queryFn: () => getResumeTarget(id!),
    enabled: !!id,
  });

  const statusMutation = useMutation({
    mutationFn: (status: ResumeTargetStatus) =>
      updateResumeTarget(id!, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["resume", "target", id] });
      queryClient.invalidateQueries({ queryKey: ["resume", "targets"] });
    },
  });

  const exportMutation = useMutation({
    mutationFn: () => downloadResumeDocx(id!),
  });

  const handleRan = () => {
    queryClient.invalidateQueries({ queryKey: ["resume", "target", id] });
    queryClient.invalidateQueries({ queryKey: ["resume", "usage"] });
    queryClient.invalidateQueries({ queryKey: ["resume", "trajectory", id] });
  };

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 12, textAlign: "center" }}>
        <CircularProgress sx={{ color: theme.palette.primary.main }} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {t("ResumeTargetWorkspace.loadingTarget")}
        </Typography>
      </Container>
    );
  }

  if (error || !target) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {(error as any)?.response?.status === 404
            ? t("ResumeTargetWorkspace.targetNotFoundError")
            : t("ResumeTargetWorkspace.loadFailedError")}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(WORKSPACE_PATH)}>
          {t("ResumeTargetWorkspace.backToWorkspaceButton")}
        </Button>
      </Container>
    );
  }

  const groups = groupSteps();
  const canExport = !!(
    target.steps["rewrite_summary"] ||
    target.steps["rewrite_experience"] ||
    target.steps["rewrite_skills"]
  );

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(WORKSPACE_PATH)}
        size="small"
        sx={{
          color: "text.secondary",
          textTransform: "none",
          fontWeight: 500,
          mb: 2.5,
          "&:hover": { color: theme.palette.primary.main, bgcolor: "transparent" },
        }}
      >
        {t("ResumeTargetWorkspace.resumeWorkspaceButton")}
      </Button>

      {/* Header */}
      <Box
        sx={{
          p: { xs: 2.5, sm: 3 },
          mb: 4,
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.primary.light, 0.2)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.06)} 0%, ${alpha(
            theme.palette.primary.light,
            0.02
          )} 100%)`,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="overline"
              sx={{ color: theme.palette.primary.main, fontWeight: 700, letterSpacing: "0.1em" }}
            >
              {t("ResumeTargetWorkspace.targetLabel")}
            </Typography>
            <Typography variant="h4" fontWeight={500} sx={{ letterSpacing: "-0.02em", mt: 0.25 }}>
              {target.company}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.25 }}>
              {target.role_title}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
            {target.gap_score != null && (
              <Chip
                label={t("ResumeTargetWorkspace.readinessChip", { score: 100 - target.gap_score })}
                sx={{
                  fontWeight: 500,
                  bgcolor: alpha(theme.palette.primary.light, 0.12),
                  color: theme.palette.primary.main,
                  fontVariantNumeric: "tabular-nums",
                }}
              />
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              disabled={!canExport || exportMutation.isPending}
              onClick={() => exportMutation.mutate()}
              title={canExport ? t("ResumeTargetWorkspace.downloadTitle") : t("ResumeTargetWorkspace.runRewriteFirstTitle")}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                flexShrink: 0,
                color: theme.palette.primary.main,
                borderColor: alpha(theme.palette.primary.light, 0.4),
                "&:hover": { borderColor: theme.palette.primary.light, bgcolor: alpha(theme.palette.primary.light, 0.04) },
              }}
            >
              {exportMutation.isPending ? t("ResumeTargetWorkspace.preparingButton") : t("ResumeTargetWorkspace.docxButton")}
            </Button>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={target.status}
                onChange={(e: SelectChangeEvent) =>
                  statusMutation.mutate(e.target.value as ResumeTargetStatus)
                }
                disabled={statusMutation.isPending}
                inputProps={{ "aria-label": t("ResumeTargetWorkspace.targetStatusAriaLabel") }}
                sx={{
                  textTransform: "capitalize",
                  fontWeight: 500,
                  bgcolor: "background.paper",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: alpha(theme.palette.primary.light, 0.3),
                  },
                }}
              >
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s} sx={{ textTransform: "capitalize" }}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      </Box>

      {exportMutation.isError && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          {t("ResumeTargetWorkspace.exportErrorMessage")}
        </Alert>
      )}

      {/* Gap-score trajectory — appears once there's a monthly check-in */}
      <Box sx={{ mb: 3 }}>
        <TrajectoryChart targetId={target.id} />
      </Box>

      {/* Exemplar panel — auto-loaded from corpus, not a workflow step */}
      <Box sx={{ mb: 3 }}>
        <ExemplarPanel targetId={target.id} />
      </Box>

      {/* Grouped steps */}
      <Stack spacing={4}>
        {groups.map(({ group, steps }) => (
          <Box key={group}>
            <Typography
              variant="overline"
              sx={{
                color: "text.secondary",
                fontWeight: 700,
                letterSpacing: "0.1em",
                display: "block",
                mb: 1.5,
              }}
            >
              {group}
            </Typography>
            <Stack spacing={1.75}>
              {steps.map((cfg) => (
                <StepCard key={cfg.key} cfg={cfg} target={target} onRan={handleRan} />
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Container>
  );
}
